import { useQuery } from "@apollo/client/react";
import { useCallback, useMemo, useRef, useState } from "react";

import { Ordering } from "@/__generated__/graphql";
import {
  SelectableCardList,
  SelectableCardListSearchField,
} from "@/components/molecules/SelectableCardList";
import {
  SelectableListItemRow,
  SelectableListItemRowTitleEnd,
} from "@/components/molecules/SelectableListItemRow";
import { Queries } from "@/constants";
import { idToIdentifier } from "@/objects/@utils";
import { State } from "@/state";
import { Utils } from "@/utils";

import {
  executionThreadNodeId,
  openExecutionThread,
} from "./utils/executionThread";

type RuleExecutionRow = {
  id: string;
  identifier: string;
  threadId: string;
  status: string | null;
  triggerType: string | null;
  error: string | null;
  startedAt: string;
  completedAt: string | null;
};

/** What the list's rows are while it loads: a typical run, drawn as any is. */
const RULE_EXECUTION_PLACEHOLDER: RuleExecutionRow = {
  id: "placeholder",
  identifier: "placeholder",
  threadId: "",
  status: "completed",
  triggerType: "transaction_created",
  error: null,
  startedAt: "2026-01-15T10:00:00Z",
  completedAt: "2026-01-15T10:00:02Z",
};

function formatDuration(start: string, end: string | null): string {
  if (!end) return "—";
  const ms = new Date(end).getTime() - new Date(start).getTime();
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  return `${Math.floor(ms / 60000)}m ${Math.round((ms % 60000) / 1000)}s`;
}

function formatTriggerTypeLabel(triggerType: string | null): string {
  if (!triggerType) return "Rule run";
  return triggerType
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export function RuleExecutionsTab() {
  const [searchTerm, setSearchTerm] = useState("");
  const searchInputRef = useRef<HTMLInputElement>(null);

  const ruleId = State((s) => s.activePanelNodeId);
  const activeEntityIdentifiers = State((s) => s.activeEntityIdentifiers);
  const activeThreadNodeId = State((s) => s.activeThreadNodeId);
  const isThreadPanelOpen = State((s) => s.isThreadPanelOpen);

  const ruleIdentifier = useMemo(
    () => (ruleId ? idToIdentifier(ruleId) : null),
    [ruleId],
  );

  const querySkipped = !ruleIdentifier;

  const { data, loading } = useQuery(
    Queries.RULE_EXECUTION_LOGS_CONNECTION_QUERY,
    {
      variables: {
        entityIdentifiers: activeEntityIdentifiers,
        ruleIdentifier,
        order: [{ field: { createdAt: Ordering.Desc } }],
        first: 50,
      },
      skip: querySkipped,
      fetchPolicy: "cache-and-network",
      notifyOnNetworkStatusChange: true,
    },
  );

  // Live updates flow through ``ObjectsChangedSubscriber`` → normalised
  // ``ExecutionListItem`` cache entries. New rows are spliced in by the Run
  // mutation and by ``applyObjectChangesToCache`` on ``ExecutionObject:created``.
  const rows = useMemo<RuleExecutionRow[]>(() => {
    const edges = (data as any)?.executionsConnection?.data?.edges ?? [];
    return edges.map((edge: any) => ({
      id: edge.node.id,
      identifier: edge.node.identifier,
      threadId: executionThreadNodeId(edge.node.thread) ?? "",
      status: edge.node.status ?? null,
      triggerType: edge.node.triggerType ?? null,
      error: edge.node.error ?? null,
      startedAt: edge.node.startedAt ?? edge.node.createdAt ?? "",
      completedAt: edge.node.completedAt ?? null,
    }));
  }, [data]);

  const filteredRows = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (row) =>
        (row.status ?? "").toLowerCase().includes(q) ||
        (row.triggerType ?? "").toLowerCase().includes(q) ||
        (row.error ?? "").toLowerCase().includes(q),
    );
  }, [rows, searchTerm]);

  const handleRowClick = useCallback((row: RuleExecutionRow) => {
    openExecutionThread(row.threadId);
  }, []);

  return (
    <div
      className="flex-1 min-h-0 w-full max-w-full flex flex-col overflow-hidden"
      data-testid="rule-executions-sidebar"
    >
      <SelectableCardListSearchField
        align="inset-rows"
        placeholder="Search status, trigger, error"
        value={searchTerm}
        onChange={setSearchTerm}
        inputRef={searchInputRef}
        inputTestId="rule-executions-sidebar-search"
        containerClassName="h-12 shrink-0 self-stretch [border-bottom:1px_solid_var(--border)]"
      />

      <SelectableCardList
        size="md"
        variant="soft"
        layout="cards"
        items={filteredRows}
        loading={loading}
        placeholderItem={RULE_EXECUTION_PLACEHOLDER}
        getItemKey={(row) => row.id}
        isItemActive={(row) =>
          activeThreadNodeId === row.threadId && isThreadPanelOpen
        }
        scrollToActiveKey={isThreadPanelOpen ? activeThreadNodeId : null}
        keyboardSourceRef={searchInputRef}
        onItemClick={handleRowClick}
        emptyState={{
          title: rows.length === 0 ? "No executions yet" : "No matches",
          description:
            rows.length === 0
              ? "Run this rule to see executions here."
              : undefined,
        }}
        renderItem={({ item: row, isLoading }) => (
          <ExecutionRow
            row={row}
            isLoading={isLoading}
            formatDuration={formatDuration}
          />
        )}
      />
    </div>
  );
}

function ExecutionRow({
  row,
  isLoading,
  formatDuration,
}: {
  row: RuleExecutionRow;
  /** The list's placeholder while it loads. */
  isLoading: boolean;
  formatDuration: (s: string, e: string | null) => string;
}) {
  const { status, completedAt, error, triggerType, startedAt } = row;

  return (
    <SelectableListItemRow
      title={formatTriggerTypeLabel(triggerType)}
      descriptionEnd={
        <div className="flex items-center gap-2 shrink-0">
          {startedAt ? (
            <SelectableListItemRowTitleEnd>
              {Utils.fTimeAgo(startedAt)}
            </SelectableListItemRowTitleEnd>
          ) : null}
          <SelectableListItemRowTitleEnd monospace>
            {formatDuration(startedAt, completedAt)}
          </SelectableListItemRowTitleEnd>
        </div>
      }
      description={[status, row.identifier].filter(Boolean).join(" · ")}
      error={error}
      emphasized
      isLoading={isLoading}
      rootDataAttrs={{
        "data-testid": "rule-execution-row",
        "data-execution-id": row.identifier,
        "data-status": status ?? "",
      }}
    />
  );
}
